import { Stack, Typography } from '@mui/material';
import Widget from '@/components/common/widget.tsx';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import { ConfigurationPlayerSection } from './configuration-player-section';
import { STUB_PLAYER_CAPS } from '@/helpers/player-type-caps';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { INFO_DISPLAYS_FEATURE } from '@/constants/feature-flags';

type RoomConfigurationWidgetProps = {
  roomPlayers: PlayerDto[];
  entityId: string;
  entityType: PlayerParentEntity;
  eventSlotId?: string | null;
  liveContext?: LiveContextDto | null;
  disabled?: boolean;
  videoPlayerId?: string | null;
  audioPlayerId?: string | null;
};

export function RoomConfigurationWidget({
  roomPlayers,
  entityId,
  entityType,
  eventSlotId,
  liveContext,
  disabled,
  videoPlayerId,
  audioPlayerId,
}: RoomConfigurationWidgetProps) {
  const t = useTranslations();
  const { hasAccess: hasInfoDisplays, isLoading: infoDisplaysLoading } = useFeatureAccess(INFO_DISPLAYS_FEATURE);
  const showInfoDisplays = !infoDisplaysLoading && hasInfoDisplays;

  return (
    <Widget title={t('flows.detail.configuration.room.title' as TranslationKey)} titleVariant="h6">
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('flows.detail.configuration.room.intro' as TranslationKey)}
        </Typography>

        <ConfigurationPlayerSection
          title={t('flows.detail.configuration.room.roomDisplays.title' as TranslationKey)}
          intro={t('flows.detail.configuration.room.roomDisplays.intro' as TranslationKey)}
          players={roomPlayers}
          playerType="roomPlayer"
          scrollable
          entityId={entityId}
          entityType={entityType}
          eventSlotId={eventSlotId}
          liveContext={liveContext}
          disabled={disabled}
          videoPlayerId={videoPlayerId}
          audioPlayerId={audioPlayerId}
        />

        {showInfoDisplays && (
          <ConfigurationPlayerSection
            title={t('flows.detail.configuration.room.infoDisplay.title' as TranslationKey)}
            intro={t('flows.detail.configuration.room.infoDisplay.intro' as TranslationKey)}
            players={[]}
            stubMode
            stubCap={STUB_PLAYER_CAPS.infoDisplay}
            scrollable
            entityId={entityId}
            entityType={entityType}
            eventSlotId={eventSlotId}
            liveContext={liveContext}
            disabled={disabled}
          />
        )}
      </Stack>
    </Widget>
  );
}
